import { Action, createReducer, on } from "@ngrx/store";
import { produce } from "immer";

import { ExchangeActions } from "../actions/exchange.actions";
import { OrderProposalActions } from "../actions/order-proposal.actions";
import { ProposalsState } from "../models/proposals-state";

const initialState: ProposalsState = {
	loading: true,
	allRetrieved: false,
	proposals: [],
	proposalsWithDetails: [],
	detailsLoadingId: null
};

const reducer = createReducer(
	initialState,
	on(ExchangeActions.proposals.getApi, (state) =>
		produce(state, (draft) => {
			draft.loading = true;
			draft.allRetrieved = false;
		})
	),
	on(ExchangeActions.proposals.getSuccess, (state, action) =>
		produce(state, (draft) => {
			draft.proposals = action.proposals;
			draft.loading = false;
		})
	),
	on(ExchangeActions.proposals.getFail, (state, action) =>
		produce(state, (draft) => {
			draft.loading = false;
			draft.error = action.error;
		})
	),
	on(ExchangeActions.proposal.getSuccess, (state, action) => mergeProposal(action, state)),
	on(OrderProposalActions.proposals.getApi, (state) =>
		produce(state, (draft) => {
			draft.loading = true;
			draft.allRetrieved = false;
		})
	),
	on(OrderProposalActions.proposals.getSuccess, (state, action) =>
		produce(state, (draft) => {
			draft.proposals = action.proposals; // Only update proposals, keep proposalsWithDetails intact
			draft.loading = false;
		})
	),
	on(OrderProposalActions.proposals.getFail, (state, action) =>
		produce(state, (draft) => {
			draft.loading = false;
			draft.error = action.error;
		})
	),
	// Mark the proposal as being refetched so `singleProposal` reports loading instead of
	// handing out the stale cached copy (see ProposalsState.detailsLoadingId).
	on(OrderProposalActions.proposalDetails.getApi, (state, action) =>
		produce(state, (draft) => {
			draft.detailsLoadingId = action.id;
		})
	),
	on(OrderProposalActions.proposalDetails.getSuccess, (state, action) =>
		mergeProposalDetails(action, state)
	),
	// The refetch failed: unblock the cached copy rather than leaving consumers on a
	// permanent spinner. They fall back to the last known details, as before.
	on(OrderProposalActions.proposalDetails.getFail, (state) =>
		produce(state, (draft) => {
			draft.detailsLoadingId = null;
		})
	),
	// This client just moved the proposal to a terminal status, so its cached details copy is
	// known-stale the moment the request succeeds. Neither transition refetches the details
	// (both navigate away, and the proposals-list refetch they trigger deliberately leaves
	// `proposalsWithDetails` intact), so drop the entry instead of letting anything read a
	// status the server no longer has.
	on(
		OrderProposalActions.denyProposal.denySuccess,
		OrderProposalActions.cancelProposal.cancelSuccess,
		(state, action) => evictProposalDetails(action.id, state)
	)
);

export function proposalReducer(state: ProposalsState | undefined, action: Action): ProposalsState {
	return reducer(state, action);
}

function mergeProposal(action, state: ProposalsState): ProposalsState {
	return produce(state, (draft) => {
		draft.proposals = getMergedProposals(action, state.proposals);
		draft.loading = false;
	});
}

function mergeProposalDetails(action, state: ProposalsState): ProposalsState {
	return produce(state, (draft) => {
		draft.proposalsWithDetails = getMergedProposals(action, state.proposalsWithDetails);
		draft.loading = false;
		draft.detailsLoadingId = null;
	});
}

/**
 * Removes one proposal's cached detail copy. `singleProposal` then reports loading until a fresh
 * fetch lands, so consumers wait for real data rather than acting on a stale status.
 */
function evictProposalDetails(id: string, state: ProposalsState): ProposalsState {
	return produce(state, (draft) => {
		draft.proposalsWithDetails = (state.proposalsWithDetails ?? []).filter(
			(proposal) => proposal.id !== id
		);
		if (draft.detailsLoadingId === id) {
			draft.detailsLoadingId = null;
		}
	});
}

function getMergedProposals(action, existingProposals: any[]): any {
	return produce(existingProposals, (draftProposals) => {
		const index = draftProposals.findIndex((proposal) => action.id === proposal.id);
		if (index !== -1) {
			draftProposals[index] = { ...draftProposals[index], ...action };
		} else {
			draftProposals.unshift(action);
		}
	});
}
